import { useState, useEffect, useRef, useCallback } from 'react';
import { paymentAPI, userAPI } from '../../services/api';
import {
  TEST_PRICE_INR,
  TEST_MRP_INR,
  TEST_TIME_LIMIT_MINUTES,
  SUPPORT_PHONE_DISPLAY,
  SUPPORT_PHONE_TEL,
} from '../../constants/app';

declare global {
  interface Window {
    Razorpay: any;
    REACT_APP_NAVIGATE: any;
  }
}

type PaymentNotice = {
  type: 'info' | 'error' | 'warning' | 'success';
  message: string;
} | null;

/** Server-computed price breakdown returned by /payments/coupons/validate */
type AppliedCoupon = {
  couponCode: string;
  discountPercent: number;
  mrp: number;
  originalAmount: number;
  discountAmount: number;
  payableAmount: number;
  payableAmountPaise: number;
  isFree: boolean;
};

function getTestId() {
  return (
    sessionStorage.getItem('submittedTestId') ||
    sessionStorage.getItem('currentTestId')
  );
}

/** Auto-generated OTP login names look like "User 4414" — never treat those as a real name. */
function isPlaceholderName(name: string | undefined | null) {
  return !name || /^User \d{4}$/i.test(name.trim());
}

function getStoredUser(): Record<string, any> {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
}

const NAME_PATTERN = /^[\p{L}][\p{L} .'-]*$/u;

export default function Payment() {
  const [loading, setLoading] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [notice, setNotice] = useState<PaymentNotice>(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const rzpRef = useRef<any>(null);

  // Test-taker name (captured before the report is generated)
  const [testTakerName, setTestTakerName] = useState<string>(() => {
    const stored = getStoredUser().name;
    return isPlaceholderName(stored) ? '' : String(stored);
  });
  const [nameError, setNameError] = useState<string | null>(null);

  // Coupon state
  const [couponInput, setCouponInput] = useState('');
  const [couponChecking, setCouponChecking] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [coupon, setCoupon] = useState<AppliedCoupon | null>(null);

  const payableAmount = coupon ? coupon.payableAmount : TEST_PRICE_INR;
  const payablePaise = coupon ? coupon.payableAmountPaise : TEST_PRICE_INR * 100;
  const isFree = !!coupon?.isFree;

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => setRazorpayLoaded(true);
    script.onerror = () => {
      console.error('Failed to load Razorpay script');
      setNotice({
        type: 'error',
        message: 'Could not load payment gateway. Check your connection and refresh the page.',
      });
    };
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const recordAbandon = useCallback(async (orderId: string | null, reason: 'cancelled' | 'failed') => {
    try {
      await paymentAPI.abandonPayment({
        orderId,
        testId: getTestId(),
        reason,
      });
    } catch (err) {
      console.warn('Could not record payment abandon:', err);
    }
  }, []);

  const resetCheckout = useCallback(() => {
    setLoading(false);
    setCheckoutOpen(false);
    rzpRef.current = null;
  }, []);

  const goToSuccess = (info: Record<string, unknown>) => {
    sessionStorage.setItem('paymentInfo', JSON.stringify({
      ...info,
      timestamp: new Date().toISOString(),
    }));
    resetCheckout();
    window.REACT_APP_NAVIGATE('/payment-success');
  };

  // ── Name capture ──────────────────────────────────────────────────────────
  const validateName = (): string | null => {
    const trimmed = testTakerName.trim().replace(/\s+/g, ' ');
    if (trimmed.length < 2) return 'Please enter your full name (it will be printed on your report).';
    if (trimmed.length > 60) return 'Name is too long (max 60 characters).';
    if (!NAME_PATTERN.test(trimmed)) return 'Name may only contain letters, spaces, dots, apostrophes and hyphens.';
    return null;
  };

  /** Persist the name on the user + test so the PDF prints it. Returns the saved name or null on failure. */
  const saveTestTakerName = async (testId: string): Promise<string | null> => {
    const err = validateName();
    if (err) {
      setNameError(err);
      return null;
    }
    const trimmed = testTakerName.trim().replace(/\s+/g, ' ');
    try {
      const res = await userAPI.saveName(trimmed, testId);
      if (!res.success) throw new Error(res.message || 'Could not save your name');
      const saved = res.data?.user?.name || trimmed;
      localStorage.setItem('user', JSON.stringify({ ...getStoredUser(), ...(res.data?.user || {}), name: saved }));
      setTestTakerName(saved);
      setNameError(null);
      return saved;
    } catch (error: any) {
      setNameError(error.message || 'Could not save your name. Please try again.');
      return null;
    }
  };

  // ── Coupon handling ───────────────────────────────────────────────────────
  const handleApplyCoupon = async () => {
    const code = couponInput.trim(); // codes are case-sensitive
    if (!code) {
      setCouponError('Enter a coupon code.');
      return;
    }
    setCouponChecking(true);
    setCouponError(null);
    try {
      const res = await paymentAPI.validateCoupon(code);
      if (res.success && res.valid) {
        setCoupon(res.data as AppliedCoupon);
        setCouponInput(res.data.couponCode);
        setNotice({ type: 'success', message: res.message });
      } else {
        setCoupon(null);
        setCouponError(res.message || 'Invalid coupon code.');
      }
    } catch (error: any) {
      setCoupon(null);
      setCouponError(error.message || 'Invalid coupon code.');
    } finally {
      setCouponChecking(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCoupon(null);
    setCouponInput('');
    setCouponError(null);
    setNotice(null);
  };

  /** 100% coupon: unlock at ₹0 on the server, never open the gateway. */
  const handleRedeemCoupon = async (testId: string, savedName: string) => {
    if (!coupon) return;
    setLoading(true);
    setNotice(null);
    try {
      const res = await paymentAPI.redeemCoupon(coupon.couponCode, testId);
      if (!res.success) {
        throw new Error(res.message || 'Could not apply coupon');
      }
      goToSuccess({
        paymentId: res.data.paymentId,
        orderId: null,
        method: 'coupon',
        couponCode: res.data.couponCode,
        amount: 0,
        originalAmount: coupon.originalAmount,
        discountAmount: coupon.discountAmount,
        testTakerName: savedName,
      });
    } catch (error: any) {
      console.error('Coupon redeem error:', error);
      setLoading(false);
      setNotice({
        type: 'error',
        message: error.message || 'Could not apply coupon. Please try again.',
      });
    }
  };

  // ── Payment handling ──────────────────────────────────────────────────────
  const handlePayment = async () => {
    const testId = getTestId();
    if (!testId) {
      setNotice({
        type: 'error',
        message: 'Test session not found. Please complete the assessment first.',
      });
      return;
    }

    // The name must be captured before the report can be generated.
    setLoading(true);
    const savedName = await saveTestTakerName(testId);
    if (!savedName) {
      setLoading(false);
      return;
    }

    if (isFree) {
      await handleRedeemCoupon(testId, savedName);
      return;
    }

    if (!razorpayLoaded) {
      setLoading(false);
      setNotice({
        type: 'warning',
        message: 'Payment gateway is still loading. Please wait a moment and try again.',
      });
      return;
    }

    setNotice(null);
    setLoading(true);

    let orderId: string | null = null;

    try {
      const orderResponse = await paymentAPI.createOrder(
        payablePaise,
        testId,
        coupon?.couponCode ?? null
      );

      if (!orderResponse.success) {
        throw new Error(orderResponse.message || 'Failed to create order');
      }

      const { orderId: createdOrderId, amount, currency, key } = orderResponse.data;
      orderId = createdOrderId;

      const userData = JSON.parse(localStorage.getItem('user') || '{}');

      const options = {
        key,
        amount,
        currency,
        name: 'Career Compass',
        description: 'Psychometric Assessment Report',
        order_id: orderId,
        prefill: {
          name: savedName || userData.name || '',
          email: userData.email || '',
          contact: userData.mobile || '',
        },
        theme: { color: '#2563eb' },
        handler: async (response: any) => {
          try {
            const verifyResponse = await paymentAPI.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              testId,
            });

            if (verifyResponse.success) {
              goToSuccess({
                paymentId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                method: 'razorpay',
                amount: payableAmount,
                couponCode: coupon?.couponCode ?? null,
                testTakerName: savedName,
              });
              return;
            }

            setNotice({
              type: 'error',
              message: verifyResponse.message || 'Payment verification failed. Contact support if amount was deducted.',
            });
          } catch (error: any) {
            console.error('Payment verification error:', error);
            setNotice({
              type: 'error',
              message: error.message || 'Payment verification failed. Contact support if amount was deducted.',
            });
          } finally {
            resetCheckout();
          }
        },
        modal: {
          ondismiss: () => {
            resetCheckout();
            setNotice({
              type: 'info',
              message: 'Payment window closed. No charge was made — tap Pay below to try again.',
            });
            recordAbandon(orderId, 'cancelled');
          },
        },
      };

      const rzp = new window.Razorpay(options);

      rzp.on('payment.failed', (response: any) => {
        const description =
          response?.error?.description ||
          response?.error?.reason ||
          'Payment failed. Please try again or use another method.';
        resetCheckout();
        setNotice({ type: 'error', message: description });
        recordAbandon(
          response?.error?.metadata?.order_id || orderId,
          'failed'
        );
      });

      rzpRef.current = rzp;
      setCheckoutOpen(true);
      rzp.open();
      setLoading(false);
    } catch (error: any) {
      console.error('Payment error:', error);
      resetCheckout();
      setNotice({
        type: 'error',
        message: error.message || 'Failed to start payment. Please try again.',
      });
      if (orderId) {
        recordAbandon(orderId, 'failed');
      }
    }
  };

  const noticeStyles = {
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    success: 'bg-green-50 border-green-200 text-green-800',
  };

  const buttonDisabled = loading || checkoutOpen || (!isFree && !razorpayLoaded);

  const renderButtonLabel = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
          {isFree ? 'Unlocking your report...' : 'Starting Checkout...'}
        </div>
      );
    }
    if (isFree) {
      return (
        <>
          <i className="ri-gift-line mr-2"></i>
          Unlock Report for ₹0
        </>
      );
    }
    if (!razorpayLoaded) {
      return (
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
          Loading Payment Gateway...
        </div>
      );
    }
    if (checkoutOpen) {
      return <>Complete payment in the Razorpay window</>;
    }
    return (
      <>
        <i className="ri-lock-line mr-2"></i>
        Pay ₹{payableAmount} Securely
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Pacifico, serif' }}>
            Career Compass
          </h1>
          <h2 className="text-2xl font-semibold text-gray-800">
            Unlock Your Personalized Report
          </h2>
          <p className="text-gray-600 mt-2">
            Your {TEST_TIME_LIMIT_MINUTES}-minute assessment is complete — unlock your full career report
          </p>
        </div>

        {notice && (
          <div className={`mb-6 p-4 rounded-xl border ${noticeStyles[notice.type]}`}>
            <p className="text-sm font-medium">{notice.message}</p>
          </div>
        )}

        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 text-white text-center">
            <div className="mb-2 flex items-baseline justify-center gap-3">
              <span className="text-2xl text-blue-200 line-through">₹{TEST_MRP_INR}</span>
              <span className="text-5xl font-bold" data-testid="selling-price">₹{TEST_PRICE_INR}</span>
            </div>
            <p className="text-blue-100 mb-4">
              One-time report unlock · Save ₹{TEST_MRP_INR - TEST_PRICE_INR}
            </p>
            <div className="bg-white/20 rounded-lg p-4">
              <h3 className="font-semibold mb-2">What you&apos;ll get:</h3>
              <ul className="text-sm space-y-1">
                <li>• Detailed Personality Analysis</li>
                <li>• Career Recommendations</li>
                <li>• Subject Selection Guide</li>
                <li>• Learning Style Assessment</li>
                <li>• Future Planning Roadmap</li>
              </ul>
            </div>
          </div>

          <div className="p-8">
            {/* Test-taker name — printed on the report */}
            <h3 className="text-xl font-semibold text-gray-900 mb-1">Your name for the report</h3>
            <p className="text-sm text-gray-500 mb-3">
              This name will be printed on your Career Compass report, so please enter it exactly as you want it to appear.
            </p>
            <div className="mb-6">
              <input
                type="text"
                value={testTakerName}
                onChange={(e) => {
                  setTestTakerName(e.target.value);
                  setNameError(null);
                }}
                placeholder="Full name of the student"
                autoComplete="name"
                maxLength={60}
                aria-label="Test-taker name"
                aria-invalid={!!nameError}
                disabled={loading || checkoutOpen}
                className={`w-full px-4 py-3 border rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 ${
                  nameError ? 'border-red-400' : 'border-gray-300'
                }`}
              />
              {nameError ? (
                <p className="text-red-600 text-sm mt-2" role="alert">
                  <i className="ri-error-warning-line mr-1"></i>
                  {nameError}
                </p>
              ) : testTakerName.trim() ? (
                <p className="text-green-700 text-sm mt-2">
                  <i className="ri-file-user-line mr-1"></i>
                  Report will be generated for <strong>{testTakerName.trim().replace(/\s+/g, ' ')}</strong>
                </p>
              ) : null}
            </div>

            {/* Coupon */}
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Have a coupon code?</h3>
            {coupon ? (
              <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-4 py-3 mb-6">
                <div className="flex items-center text-green-800">
                  <i className="ri-coupon-3-line w-5 h-5 flex items-center justify-center mr-2"></i>
                  <span className="font-semibold tracking-wider">{coupon.couponCode}</span>
                  <span className="ml-2 text-sm">— {coupon.discountPercent}% off applied</span>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveCoupon}
                  disabled={loading}
                  className="text-sm text-green-700 hover:text-green-900 underline cursor-pointer disabled:cursor-not-allowed"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="mb-6">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponInput}
                    onChange={(e) => {
                      setCouponInput(e.target.value);
                      setCouponError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleApplyCoupon();
                      }
                    }}
                    placeholder="Enter coupon code"
                    autoCapitalize="off"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    aria-label="Coupon code"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl tracking-wider font-semibold font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={handleApplyCoupon}
                    disabled={couponChecking || !couponInput.trim()}
                    className="px-6 py-3 bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 text-white rounded-xl font-semibold whitespace-nowrap cursor-pointer disabled:cursor-not-allowed transition-colors"
                  >
                    {couponChecking ? 'Checking...' : 'Apply'}
                  </button>
                </div>
                {couponError && (
                  <p className="text-red-600 text-sm mt-2" role="alert">
                    <i className="ri-error-warning-line mr-1"></i>
                    {couponError}
                  </p>
                )}
              </div>
            )}

            {/* Order summary */}
            <div className="border border-gray-200 rounded-xl p-4 mb-6 text-sm">
              <div className="flex justify-between text-gray-500 mb-1">
                <span>Original price (MRP)</span>
                <span className="line-through">₹{TEST_MRP_INR}</span>
              </div>
              <div className="flex justify-between text-gray-700 mb-1">
                <span>Selling price</span>
                <span>₹{TEST_PRICE_INR}</span>
              </div>
              {coupon && (
                <div className="flex justify-between text-green-700 mb-1">
                  <span>Coupon {coupon.couponCode} ({coupon.discountPercent}% off)</span>
                  <span>− ₹{coupon.discountAmount}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-900 font-bold text-base border-t border-gray-200 pt-2 mt-2">
                <span>Amount payable</span>
                <span data-testid="payable-amount">₹{payableAmount}</span>
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              {isFree ? 'No payment required' : 'Secure Payment'}
            </h3>

            <div className={`border rounded-xl p-4 mb-6 ${isFree ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
              <p className={`text-sm ${isFree ? 'text-green-800' : 'text-blue-800'}`}>
                <i className="ri-information-line mr-2"></i>
                {isFree
                  ? 'Your coupon covers the full amount. Tap below to unlock your report instantly — no payment gateway, no charge.'
                  : 'Pay via UPI, cards, net banking, or wallets. Closing the payment window does not charge you — you can retry anytime.'}
              </p>
            </div>

            <button
              onClick={handlePayment}
              disabled={buttonDisabled}
              className={`w-full text-white py-4 rounded-xl text-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 disabled:transform-none whitespace-nowrap cursor-pointer disabled:cursor-not-allowed disabled:bg-gray-400 ${
                isFree ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {renderButtonLabel()}
            </button>

            {!isFree && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-center text-green-800">
                  <i className="ri-shield-check-line w-5 h-5 flex items-center justify-center mr-2"></i>
                  <span className="font-semibold">Secure Payment</span>
                </div>
                <p className="text-green-700 text-sm mt-1">
                  Powered by Razorpay. Your card/UPI details are never stored on our servers.
                </p>
              </div>
            )}

            <p className="mt-6 text-center text-sm text-gray-500">
              Need help?{' '}
              <a href={SUPPORT_PHONE_TEL} className="text-blue-600 hover:text-blue-700 font-medium">
                Call {SUPPORT_PHONE_DISPLAY}
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
